import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canSeeAdminCenter } from "@/lib/roles";
import { Loader2, ShieldCheck } from "lucide-react";
import AuthShell from "@/components/AuthShell";
import MfaChallenge from "@/components/MfaChallenge";
import { MfaEnroll } from "@/components/MfaEnroll";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser, refresh } = useAuth();
  const processed = useRef(false);
  const [mfaMode, setMfaMode] = useState(null); // null | "challenge" | "setup"

  const finish = (data) => {
    setUser(data);
    window.history.replaceState(null, "", "/dashboard");
    navigate(canSeeAdminCenter(data.role) ? "/admin" : "/dashboard", { replace: true });
  };

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;
    const isReauth = new URLSearchParams(window.location.search).get("reauth") === "1";

    // ---- Google reauthentication return (high-risk MFA action step-up) ----
    if (isReauth) {
      const runReauth = async () => {
        let intent = null;
        try { intent = JSON.parse(sessionStorage.getItem("mfa_reauth_intent") || "null"); } catch { intent = null; }
        const returnTo = intent?.returnTo || "/security";
        sessionStorage.removeItem("mfa_reauth_intent");
        if (!sessionId || !intent) {
          toast.error("Reauthentication cancelled.");
          await refresh();
          window.history.replaceState(null, "", returnTo);
          navigate(returnTo, { replace: true });
          return;
        }
        try {
          const { data } = await api.post("/auth/mfa/reauth/google", { action: intent.action }, { headers: { "X-Reauth-Session-ID": sessionId } });
          sessionStorage.setItem("mfa_reauth_token", JSON.stringify({ action: intent.action, token: data.reauth_token }));
        } catch {
          toast.error("Google reauthentication failed. Please try again.");
        }
        await refresh();
        window.history.replaceState(null, "", returnTo);
        navigate(returnTo, { replace: true });
      };
      runReauth();
      return;
    }

    // ---- Normal Google login ----
    const run = async () => {
      if (!sessionId) { navigate("/login"); return; }
      try {
        const { data } = await api.post("/auth/google/session", {}, { headers: { "X-Session-ID": sessionId } });
        if (data.mfa_required) { setMfaMode("challenge"); return; }
        if (data.mfa_enrollment_required) { setMfaMode("setup"); return; }
        finish(data);
      } catch {
        navigate("/login");
      }
    };
    run();
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  if (mfaMode === "challenge") {
    return (
      <AuthShell title="Verify it's you" subtitle="One more step to keep your account secure.">
        <MfaChallenge onSuccess={finish} onCancel={() => navigate("/login")} />
      </AuthShell>
    );
  }
  if (mfaMode === "setup") {
    return (
      <AuthShell title="Two-factor setup required" subtitle="Your role requires two-factor authentication before you can continue.">
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800" data-testid="mfa-forced-setup-notice">
          <ShieldCheck size={16} /> Set up an authenticator app now to finish signing in.
        </div>
        <MfaEnroll onDone={(res) => (res?.user ? finish(res.user) : navigate("/login"))} onCancel={() => navigate("/login")} />
      </AuthShell>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
      <div className="flex flex-col items-center gap-3 text-slate-600">
        <Loader2 className="animate-spin" size={28} />
        <p className="text-sm">Signing you in…</p>
      </div>
    </div>
  );
}
